import { eventDayCutoff } from '@shared/dayCutoff'
import { entryInterestIds } from '@shared/lineupActs'
import { useEffect, useMemo } from 'react'
import { doc, collection, query, where } from 'firebase/firestore'
import type { DocumentReference, Query } from 'firebase/firestore'
import { db, isOtaBoot } from '../lib/firebase'
import { getDocs, getDocsCacheFirst } from '../lib/firestore'
import { useFirestoreDoc } from './useFirestore'
import { useDebouncedValue } from './useDebouncedValue'
import { useResetOnChange } from './useResetOnChange'
import { useArtistsByIds } from './useArtistsByIds'
import { eventDayRange } from '../lib/formatDate'
import { useCurrentFestivalDay } from './useCurrentFestivalDay'
import { classifyError, type ClassifiedError } from '../lib/errors'
import { compareStageOrder } from '../lib/utils'
import { isTimedSet } from '../lib/eventUtils'
import { eventContentVersion } from '../lib/eventVersion'
import type { Event, Stage, LineupEntry, Set as FestivalSet } from '../types'

const EMPTY_STAGES: Stage[] = []
const EMPTY_LINEUP: LineupEntry[] = []
const EMPTY_SETS: FestivalSet[] = []

// Module-level caches: survive unmount/remount so returning to a previously
// visited event shows data on the first render instead of skeletons.
const stagesLineupMemCache = new Map<string, { stages: Stage[]; lineup: LineupEntry[] }>()
const setsMemCache = new Map<string, FestivalSet[]>()

// Rapid version bumps (e.g. seeding a festival) collapse into one refetch.
const VERSION_DEBOUNCE_MS = 5_000

function versionKey(eventId: string): string {
  return `event:${eventId}:v`
}

export function useEventDetail(eventId: string) {
  const eventQ = useFirestoreDoc<Event>(doc(db, 'events', eventId) as DocumentReference<Event>)
  // firestore.rules gates `events/{id}` reads on `published`, so a denied read
  // means exactly one thing: the event was unpublished (e.g. an admin rejected
  // it from the review queue) and we aren't an admin. To a visitor that's
  // indistinguishable from a deleted event, so drop the now-stale cached copy
  // and report it as missing instead of as a permissions failure. Admins read
  // unpublished events fine and never reach this branch.
  const eventUnpublished = eventQ.error?.kind === 'permission'
  const event = eventUnpublished ? null : eventQ.data
  const contentVersion = event ? eventContentVersion(event) : undefined
  const stableVersion = useDebouncedValue(contentVersion, VERSION_DEBOUNCE_MS, eventId)
  const eventLoading = eventQ.loading
  const hasSchedule = event?.hasSchedule === true

  const officialDays = event ? eventDayRange(event.startDate, event.endDate) : []

  // Reset (during render) when the event changes: EventPage stays mounted from
  // a festival to one of its afters, and the previous event's lineup, loading
  // flags and errors must not paint under the new header.
  const [stages, setStages] = useResetOnChange<Stage[]>(eventId, () => stagesLineupMemCache.get(eventId)?.stages ?? EMPTY_STAGES)
  const [lineup, setLineup] = useResetOnChange<LineupEntry[]>(eventId, () => stagesLineupMemCache.get(eventId)?.lineup ?? EMPTY_LINEUP)
  const [stagesLineupLoading, setStagesLineupLoading] = useResetOnChange(eventId, () => !stagesLineupMemCache.has(eventId))
  const [stagesLineupError, setStagesLineupError] = useResetOnChange<ClassifiedError | null>(eventId, () => null)

  const [allSets, setAllSets] = useResetOnChange<FestivalSet[]>(eventId, () => setsMemCache.get(eventId) ?? EMPTY_SETS)
  const [setsLoading, setSetsLoading] = useResetOnChange(eventId, () => !setsMemCache.has(eventId))
  const [setsError, setSetsError] = useResetOnChange<ClassifiedError | null>(eventId, () => null)

  useEffect(() => {
    if (eventLoading) return
    let cancelled = false

    const cachedV = localStorage.getItem(versionKey(eventId))
    const serverV = stableVersion !== undefined ? String(stableVersion) : null
    const fresh = serverV !== null && cachedV === serverV
    // The version key is only written after a successful network fetch, so its
    // absence means this event's cache was never populated — an empty cache
    // result then means "no data yet" (keep the skeleton), not "no lineup".
    const everSynced = cachedV !== null

    const stagesRef = collection(db, 'events', eventId, 'stages') as Query<Stage>
    const lineupRef = query(collection(db, 'lineup'), where('eventId', '==', eventId)) as Query<LineupEntry>

    // Always cache-first so the page renders immediately on slow networks
    // instead of blocking on a getDocs network round-trip that may hang.
    Promise.all([
      getDocsCacheFirst(stagesRef),
      getDocsCacheFirst(lineupRef),
    ])
      .then(([stagesSnap, lineupSnap]) => {
        if (cancelled) return
        const stagesArr = stagesSnap.docs.map(d => ({ ...d.data(), id: d.id }) as Stage)
        stagesArr.sort(compareStageOrder)
        const lineupArr = lineupSnap.docs.map(d => ({ ...d.data(), id: d.id }) as LineupEntry)
        stagesLineupMemCache.set(eventId, { stages: stagesArr, lineup: lineupArr })
        setStages(stagesArr)
        setLineup(lineupArr)

        const cacheEmpty = stagesSnap.empty && lineupSnap.empty
        const coldCache = cacheEmpty && !everSynced
        if (!coldCache) setStagesLineupLoading(false)

        // A fully-empty cache result on a synced event can mean evicted
        // IndexedDB rather than "party with no schedule" — refresh in the
        // background even when the version key says fresh.
        if (fresh && !cacheEmpty) {
          if (serverV && !isOtaBoot) localStorage.setItem(versionKey(eventId), serverV)
        } else {
          Promise.all([getDocs(stagesRef), getDocs(lineupRef)])
            .then(([s, l]) => {
              if (cancelled) return
              const freshStages = s.docs.map(d => ({ ...d.data(), id: d.id }) as Stage)
              freshStages.sort(compareStageOrder)
              const freshLineup = l.docs.map(d => ({ ...d.data(), id: d.id }) as LineupEntry)
              stagesLineupMemCache.set(eventId, { stages: freshStages, lineup: freshLineup })
              setStages(freshStages)
              setLineup(freshLineup)
              setStagesLineupLoading(false)
              if (serverV && !isOtaBoot) localStorage.setItem(versionKey(eventId), serverV)
            })
            .catch(err => {
              if (cancelled || !coldCache) return
              setStagesLineupError(classifyError(err))
              setStagesLineupLoading(false)
            })
        }
      })
      .catch(err => {
        if (cancelled) return
        setStagesLineupError(classifyError(err))
        setStagesLineupLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [eventId, stableVersion, eventLoading, setStages, setLineup, setStagesLineupLoading, setStagesLineupError])

  useEffect(() => {
    if (eventLoading) return
    let cancelled = false

    const cachedV = localStorage.getItem(versionKey(eventId))
    const serverV = stableVersion !== undefined ? String(stableVersion) : null
    const fresh = serverV !== null && cachedV === serverV
    const everSynced = cachedV !== null

    const setsRef = query(
      collection(db, 'sets'),
      where('eventId', '==', eventId),
    ) as Query<FestivalSet>

    getDocsCacheFirst(setsRef)
      .then(snap => {
        if (cancelled) return
        const setsArr = snap.docs.map(d => ({ ...d.data(), id: d.id }) as FestivalSet)
        setsMemCache.set(eventId, setsArr)
        setAllSets(setsArr)

        // Empty + never synced = cold cache: keep the skeleton until the
        // network fetch lands (see the stages/lineup effect).
        const coldCache = snap.empty && !everSynced
        if (!coldCache) setSetsLoading(false)

        if (!fresh || snap.empty) {
          getDocs(setsRef)
            .then(freshSnap => {
              if (cancelled) return
              const freshSets = freshSnap.docs.map(d => ({ ...d.data(), id: d.id }) as FestivalSet)
              setsMemCache.set(eventId, freshSets)
              setAllSets(freshSets)
              setSetsLoading(false)
            })
            .catch(err => {
              if (cancelled || !coldCache) return
              setSetsError(classifyError(err))
              setSetsLoading(false)
            })
        }
      })
      .catch(err => {
        if (cancelled) return
        setSetsError(classifyError(err))
        setSetsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [eventId, stableVersion, eventLoading, setAllSets, setSetsLoading, setSetsError])

  // Seeds occasionally land sets without an end time; they can't be placed on
  // the grid, so no schedule surface should ever see them.
  const sets = useMemo(() => allSets.filter(isTimedSet), [allSets])

  // Show only days that have at least one set — includes pre-party days
  // outside the official range, and excludes date-range days with no sets.
  const days = useMemo(() => {
    if (!event) return []
    if (sets.length === 0) return eventDayRange(event.startDate, event.endDate)
    return [...new Set(sets.map(s => s.day))].sort()
  }, [event, sets])

  // The day a day-tabbed view opens on: the current (clock-aware) festival day
  // when it's an actual schedule day — including pre-party days outside the
  // official range (e.g. a Thursday with sets). "Festival day" and not the
  // calendar date: at 2am you're still on last night's schedule, so it holds
  // there until the day cutoff. Only when today isn't part of the festival do we
  // fall back to the official start day. Views own the day the user picks from
  // there, so it resets when they leave (see ScheduleView / LineupView).
  const today = useCurrentFestivalDay(eventDayCutoff(event))
  const defaultDay = days.includes(today) ? today : (officialDays[0] ?? null)

  // Two stable artist queries instead of one re-keying union: lineup IDs are
  // canonical; the second query only fires for set artists not in lineup
  // (rare). This avoids batch re-shuffling when sets resolves after lineup.
  // Acts load every performer plus the named collab they perform as.
  const lineupArtistIds = lineup.flatMap(entryInterestIds)
  const lineupArtistIdSet = new Set(lineupArtistIds)
  const extraArtistIds = sets.flatMap(s =>
    entryInterestIds(s).filter(id => !lineupArtistIdSet.has(id)),
  )
  const lineupArtistsQ = useArtistsByIds(lineupArtistIds)
  const extraArtistsQ = useArtistsByIds(extraArtistIds)
  // Every memoized SetBlock and festival mode's outlet context take this map —
  // a fresh object per render re-rendered the whole grid on each snapshot.
  const artists = useMemo(
    () => ({ ...lineupArtistsQ.artists, ...extraArtistsQ.artists }),
    [lineupArtistsQ.artists, extraArtistsQ.artists],
  )
  const artistsLoading = lineupArtistsQ.loading || extraArtistsQ.loading
  const artistsError = lineupArtistsQ.error ?? extraArtistsQ.error

  // loading means "nothing to display yet" (see useFirestore.ts).
  // eventQ.loading is already cache-aware via the base hook; the manual
  // fetches (stages/lineup) use the same pattern here.
  const loading = eventQ.loading || (stagesLineupLoading && stages.length === 0 && lineup.length === 0)
  const error =
    (eventUnpublished ? null : eventQ.error) ??
    stagesLineupError ??
    setsError ??
    artistsError ??
    null

  return {
    event,
    stages,
    artists,
    artistsLoading,
    lineup,
    allSets: sets,
    setCount: allSets.length,
    setsMissingTimes: allSets.length - sets.length,
    setsLoading: setsLoading && allSets.length === 0,
    lineupLoading: stagesLineupLoading && lineup.length === 0,
    hasSchedule,
    days,
    defaultDay,
    loading,
    error,
  }
}
